import React from 'react';
import { formatCurrency } from '../../../../../../utils/functions';
import { SalesStatistics } from './../../types';
import { FaArrowTrendUp, FaArrowTrendDown } from 'react-icons/fa6';
import { FaFire, FaCalendarDay, FaCalendarWeek, FaCalendar, FaReceipt } from 'react-icons/fa';
import GraficoVentas from './components/GraficoVentas/GraficoVentas';
import Table from './components/Table/Table';
import { generateGeneralSalesFakeData } from './fakeData';

interface GeneralSalesProps {
  salesData: SalesStatistics[];
}

const GeneralSales: React.FC<GeneralSalesProps> = ({ salesData }) => {
  const demoMode = (typeof window !== 'undefined') && (localStorage.getItem('demoMode') === 'true');
  const fakeData = React.useMemo(() => demoMode ? generateGeneralSalesFakeData({ days: 360 }) : null, [demoMode]);
  const effectiveSales = demoMode && fakeData ? fakeData : salesData;

  const latestSession = effectiveSales.length > 0
    ? effectiveSales.reduce((latest, current) => {
        const latestId = (latest as any).session_id ?? 0;
        const currentId = (current as any).session_id ?? 0;
        return currentId > latestId ? current : latest;
      })
    : undefined;
  const lastNet = latestSession ? latestSession.net_sales : 0;

  const totalNetSum = effectiveSales.reduce((acc, stat) => acc + stat.net_sales, 0);

  const getDayKey = (ts: string) => new Date(ts).toISOString().slice(0, 10);
  const getWeekKey = (ts: string) => {
    const d = new Date(ts);
    const target = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    const dayNum = target.getUTCDay() === 0 ? 7 : target.getUTCDay();
    target.setUTCDate(target.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(target.getUTCFullYear(), 0, 1));
    const weekNo = Math.ceil((((target.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
    return `${target.getUTCFullYear()}-W${weekNo}`;
  };
  const getMonthKey = (ts: string) => {
    const d = new Date(ts);
    return `${d.getFullYear()}-${('0' + (d.getMonth() + 1)).slice(-2)}`;
  };

  const daySet = new Set(effectiveSales.map((s) => getDayKey(s.timestamp)));
  const weekSet = new Set(effectiveSales.map((s) => getWeekKey(s.timestamp)));
  const monthSet = new Set(effectiveSales.map((s) => getMonthKey(s.timestamp)));

  const avgPerDay = daySet.size > 0 ? totalNetSum / daySet.size : 0;
  const avgPerWeek = weekSet.size > 0 ? totalNetSum / weekSet.size : 0;
  const avgPerMonth = monthSet.size > 0 ? totalNetSum / monthSet.size : 0;
  const avgTicket = effectiveSales.length > 0
    ? effectiveSales.reduce((acc, s) => acc + s.avg_ticket, 0) / effectiveSales.length : 0;

  const pctChange = avgPerDay === 0 ? 0 : ((lastNet - avgPerDay) / avgPerDay) * 100;
  const isPctPositive = pctChange > 0;

  const METRICS = [
    {
      icon: FaFire, iconClass: '', label: 'Última Sesión',
      value: formatCurrency(lastNet),
      variation: { positive: isPctPositive, text: `${Math.abs(pctChange).toFixed(1)}%` },
    },
    {
      icon: FaCalendarDay, iconClass: 'mp-stat-card-icon-blue', label: 'Promedio por Día',
      value: formatCurrency(avgPerDay), subtitle: `Sobre ${daySet.size} día(s)`,
    },
    {
      icon: FaCalendarWeek, iconClass: 'mp-stat-card-icon-purple', label: 'Promedio Semanal',
      value: formatCurrency(avgPerWeek), subtitle: `Sobre ${weekSet.size} semana(s)`,
    },
    {
      icon: FaCalendar, iconClass: 'mp-stat-card-icon-pink', label: 'Promedio por Mes',
      value: formatCurrency(avgPerMonth), subtitle: `Sobre ${monthSet.size} mes(es)`,
    },
    {
      icon: FaReceipt, iconClass: 'mp-stat-card-icon-green', label: 'Ticket Promedio',
      value: formatCurrency(avgTicket), subtitle: `Sobre ${effectiveSales.length} sesión(es)`,
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      {/* KPI Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
        {METRICS.map((m) => (
          <div key={m.label} className="mp-stat-card">
            <div className={`mp-stat-card-icon ${m.iconClass}`}>
              <m.icon />
            </div>
            <div className="flex flex-col min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="mp-stat-card-label">{m.label}</span>
                {m.variation && (
                  <span className={`mp-stat-card-variation ${m.variation.positive ? 'mp-stat-card-variation-up' : 'mp-stat-card-variation-down'}`}>
                    {m.variation.positive ? <FaArrowTrendUp /> : <FaArrowTrendDown />}
                    {m.variation.text}
                  </span>
                )}
              </div>
              <span className="mp-stat-card-value">{m.value}</span>
              {m.subtitle && <span className="mp-stat-card-subtitle">{m.subtitle}</span>}
            </div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <GraficoVentas salesData={effectiveSales} />

      {/* Session History */}
      <Table data={effectiveSales} />
    </div>
  );
};

export default GeneralSales;
